import React, { useState } from "react";
import { FixVersionSummaryByCategory } from "../../global/Types";

interface ReleaseVersionsSummaryBarProps {
    summary: FixVersionSummaryByCategory;
}

const STATUS_CATEGORY_COLORS: Record<string, { dot: string; text: string; bg: string }> = {
    "done": { dot: "bg-green-500", text: "text-green-700", bg: "bg-green-50" },
    "in progress": { dot: "bg-yellow-400", text: "text-yellow-700", bg: "bg-yellow-50" },
    "to do": { dot: "bg-gray-400", text: "text-gray-600", bg: "bg-gray-50" },
};

const getCategoryStyle = (category: string) =>
    STATUS_CATEGORY_COLORS[category.toLowerCase()] ?? { dot: "bg-indigo-400", text: "text-indigo-700", bg: "bg-indigo-50" };

const ReleaseVersionsSummaryBar: React.FC<ReleaseVersionsSummaryBarProps> = ({ summary }) => {
    const [isExpanded, setIsExpanded] = useState(false);

    return (
        <div className="mb-4 rounded-lg border border-indigo-100 bg-white shadow-sm px-4 py-3">
            {/* Collapsed row */}
            <div className="flex items-center gap-4 flex-wrap">
                <span className="text-sm font-semibold text-indigo-700">
                    {summary.total} ticket{summary.total !== 1 ? "s" : ""}
                </span>
                <div className="w-px h-4 bg-indigo-100" />
                {Object.entries(summary.by_status_category).map(([category, count]) => {
                    const style = getCategoryStyle(category);
                    return (
                        <span key={category} className="flex items-center gap-1.5">
                            <span className={`inline-block w-2 h-2 rounded-full ${style.dot}`} />
                            <span className={`text-sm ${style.text}`}>{count} {category}</span>
                        </span>
                    );
                })}
                <button
                    className="ml-auto text-xs text-indigo-500 hover:text-indigo-700 font-medium flex items-center gap-1 transition-colors"
                    onClick={() => setIsExpanded(prev => !prev)}
                >
                    {isExpanded ? "▲" : "▼"}
                </button>
            </div>

            {/* Expanded breakdown */}
            {isExpanded && (
                <div className="mt-3 pt-3 border-t border-indigo-50 flex flex-wrap gap-6">
                    {Object.entries(summary.by_status_per_category).map(([category, statuses]) => {
                        const style = getCategoryStyle(category);
                        return (
                            <div key={category} className="min-w-max">
                                <div className="flex items-center gap-1.5 mb-1.5">
                                    <span className={`inline-block w-2 h-2 rounded-full ${style.dot}`} />
                                    <span className={`text-xs font-semibold uppercase tracking-wide ${style.text}`}>
                                        {category} ({summary.by_status_category[category]})
                                    </span>
                                </div>
                                <div className="flex flex-col gap-1 pl-3.5">
                                    {Object.entries(statuses).map(([status, count]) => (
                                        <div key={status} className="flex items-center justify-between gap-6">
                                            <span className="text-xs text-gray-500">{status}</span>
                                            <span className={`text-xs font-semibold px-1.5 py-0.5 rounded ${style.bg} ${style.text}`}>
                                                {count}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default ReleaseVersionsSummaryBar;